import objectPath from 'object-path'
import { getCurrentUi } from '../Data'
import { createCallAPIActionList } from './CallApi.util'

const DEBUG_STORAGE_KEY = 'CKD_CONDITION_DEBUG'
const MAX_TRACE_SIZE = 100
const NO_WAIT_CALL_API_MODES = ['Background', 'No Wait', 'NoWait']
const NON_UI_CONDITION_LAYOUTS = [
  'urlParam',
  'localStorage',
  'condition',
  'datasources',
  'response',
  'user',
  'perspective',
]
const UI_ACTION_STATES = [
  'isShow',
  'isHide',
  'isShowHideList',
  'isEnable',
  'isDisable',
  'isSet',
  'isReset',
  'isColorLabel',
  'isCalculate',
  'updateOption',
  'fetchComponent',
  'fetchTable',
  'updateCheckboxList',
  'updateValue',
  'activeButton',
  'activeTab',
  'clearActive',
  'clearActiveButton',
  'clearActiveTab',
  'isWarning',
  'isRequire',
  'dateToAge',
  'dateToText',
  'ageToDate',
  'openModal',
  'isLoading',
  'isDoneLoading',
  'unselectRowTable',
  'selectRowTable',
]

let debugCache = {}

const getBrowserRoute = () => {
  if (typeof window === 'undefined') return ''
  return [window.location?.pathname, window.location?.hash].join('')
}

const getDesignCode = (designContext = {}) =>
  objectPath.get(designContext, 'formDesign.code') ||
  objectPath.get(designContext, 'jsonForm.code') ||
  objectPath.get(designContext, 'jsonForm.formCode') ||
  'unknown'

export const isConditionActionDebugEnabled = (designContext = {}) => {
  if (process.env.NODE_ENV === 'production') return false
  if (designContext.conditionActionDebug === true) return true
  if (designContext.conditionActionDebug?.enabled === true) return true

  let route = getBrowserRoute()
  if (route.includes('/designer/')) return true

  if (typeof window === 'undefined') return false
  try {
    return window.localStorage?.getItem(DEBUG_STORAGE_KEY) === 'true'
  } catch (error) {
    return false
  }
}

const isWaitCallApiAction = (action = {}) =>
  action.state === 'callAPI' && !NO_WAIT_CALL_API_MODES.includes(action.mode)

const getWaitModeLabel = (action = {}) => {
  if (action.state !== 'callAPI') return null
  return isWaitCallApiAction(action) ? 'Wait' : action.mode || 'NoWait'
}

const getConditionSource = (condition = {}) => ({
  key: condition.key,
  name: condition.name || condition.valueKey,
  state: condition.state,
  layout: condition.layout,
  groupType: condition.groupType,
})

const getActionSummary = (action = {}) => ({
  key: action.key,
  state: action.state,
  mode: action.mode,
  waitModeLabel: getWaitModeLabel(action),
})

const getConditionBranches = (setting = {}, settingIndex) => {
  if (setting.isIfElse) {
    return (setting.ifElse || []).map((branch = {}, branchIndex) => ({
      setting,
      settingIndex,
      branchIndex,
      conditions: branch.conditions || [],
      actions: branch.actions || [],
    }))
  }

  return [
    {
      setting,
      settingIndex,
      branchIndex: null,
      conditions: setting.conditions || [],
      actions: setting.actions || [],
    },
  ]
}

export const createConditionActionSummary = (jsonForm = {}) => {
  let conditions = objectPath.get(jsonForm, 'conditions') || []

  return conditions.map((setting = {}, settingIndex) => ({
    key: setting.key,
    name: setting.name,
    settingIndex,
    isEnabled: setting.isEnabled,
    isIfElse: setting.isIfElse,
    branches: getConditionBranches(setting, settingIndex).map(
      ({ branchIndex, conditions = [], actions = [] }) => ({
        branchIndex,
        sources: conditions.map(getConditionSource),
        triggers: conditions
          .map(({ state }) => state)
          .filter((state) =>
            ['isOnClick', 'isOnChange', 'isAfterSuccess'].includes(state),
          ),
        actions: actions.map(getActionSummary),
      }),
    ),
  }))
}

const hasFormKey = (jsonForm = {}, key) => {
  if (!key || key === 'all') return true
  if (objectPath.has(jsonForm, ['schema', 'mapKey', key])) return true
  if (objectPath.has(jsonForm, ['schema', 'properties', key])) return true
  if (objectPath.has(jsonForm, ['formData', key])) return true
  return false
}

const hasUiKey = (jsonForm = {}, designContext = {}, key) => {
  if (hasFormKey(jsonForm, key)) return true
  try {
    let ui = getCurrentUi(
      key,
      jsonForm.schema || {},
      jsonForm.uischema || {},
      designContext.isUiChanged,
    )
    return ui?.key === key
  } catch (error) {
    return false
  }
}

const createWarning = (type, message, detail = {}) => ({
  type,
  message,
  ...detail,
})

const validateConditionSource = (jsonForm, designContext, source, meta) => {
  if (!source.key) return []
  if (
    NON_UI_CONDITION_LAYOUTS.includes(source.layout) ||
    source.groupType === 'response'
  ) {
    return []
  }
  if (hasFormKey(jsonForm, source.key)) return []

  return [
    createWarning(
      'missingConditionSource',
      'Condition source key was not found in schema or formData.',
      { ...meta, sourceKey: source.key, sourceState: source.state },
    ),
  ]
}

const validateActionTarget = (jsonForm, designContext, action, meta) => {
  if (!action.state) return []

  if (action.state === 'callAPI') {
    let apiGroup = createCallAPIActionList(jsonForm)
    let api = apiGroup.find(({ key }) => key === action.key)
    if (!action.key || api) return []

    return [
      createWarning(
        'missingCallApiAction',
        'callAPI action key was not found in the form api group.',
        { ...meta, actionKey: action.key, actionState: action.state },
      ),
    ]
  }

  if (!UI_ACTION_STATES.includes(action.state) || !action.key) return []
  if (hasUiKey(jsonForm, designContext, action.key)) return []

  return [
    createWarning(
      'missingActionTarget',
      'Action target key was not found in uischema.',
      { ...meta, actionKey: action.key, actionState: action.state },
    ),
  ]
}

const validateActionRisk = (action, actions, actionIndex, meta) => {
  let warnings = []

  if (['openModal', 'isWarning'].includes(action.state)) {
    warnings.push(
      createWarning(
        'riskActionBehavior',
        'Action has guarded runtime behavior and runs only when condition passes.',
        { ...meta, actionKey: action.key, actionState: action.state },
      ),
    )
  }

  if (isWaitCallApiAction(action) && actions.length > actionIndex + 1) {
    warnings.push(
      createWarning(
        'waitCallApiHasNextAction',
        'Wait callAPI has following actions. Review whether the next actions depend on response data.',
        { ...meta, actionKey: action.key, actionState: action.state },
      ),
    )
  }

  return warnings
}

export const validateConditionActionSummary = (
  jsonForm = {},
  designContext = {},
) => {
  let conditions = objectPath.get(jsonForm, 'conditions') || []
  let warnings = []

  conditions.forEach((setting = {}, settingIndex) => {
    getConditionBranches(setting, settingIndex).forEach(
      ({ branchIndex, conditions = [], actions = [] }) => {
        let meta = {
          conditionKey: setting.key,
          settingIndex,
          branchIndex,
        }

        conditions.forEach((condition = {}) => {
          warnings = warnings.concat(
            validateConditionSource(
              jsonForm,
              designContext,
              getConditionSource(condition),
              meta,
            ),
          )
        })

        actions.forEach((action = {}, actionIndex) => {
          warnings = warnings.concat(
            validateActionTarget(jsonForm, designContext, action, meta),
            validateActionRisk(action, actions, actionIndex, meta),
          )
        })
      },
    )
  })

  return warnings
}

export const publishConditionActionDebug = (designContext = {}) => {
  if (!isConditionActionDebugEnabled(designContext)) return

  let jsonForm = designContext.jsonForm || {}
  let designCode = getDesignCode(designContext)
  let conditions = objectPath.get(jsonForm, 'conditions') || []
  let cacheKey = [designCode, conditions.length].join(':')
  if (debugCache[cacheKey]) return

  let summary = createConditionActionSummary(jsonForm)
  let warnings = validateConditionActionSummary(jsonForm, designContext)
  debugCache[cacheKey] = true
  designContext.conditionActionDebug = { enabled: true, summary, warnings }

  if (typeof window !== 'undefined') {
    window.__CKD_CONDITION_ACTION_DEBUG__ = {
      designCode,
      summary,
      warnings,
    }
  }

  if (warnings.length > 0) {
    console.warn('[ConditionActionConfig]', { designCode, warnings, summary })
    return
  }

  console.debug('[ConditionActionConfig]', { designCode, summary })
}

export const getConditionActionTrigger = (status = {}) => {
  if (status.isOnClick) return 'onClick'
  if (status.isOnChange) return 'onChange'
  if (status.isAfterSuccess) return 'afterSuccess'
  if (status.isCheckOnlyResponse) return 'response'
  if (status.isCheckOnlyUrlParam) return 'urlParam'
  if (status.isActive) return 'active'
  if (status.isSelectRow) return 'selectRow'
  if (status.isAddRow) return 'addRow'
  if (status.isEditRow) return 'editRow'
  if (status.isDeleteRow) return 'deleteRow'
  return 'default'
}

export const traceConditionAction = (designContext = {}, event = {}) => {
  if (!isConditionActionDebugEnabled(designContext)) return

  let traceEvent = {
    at: new Date().toISOString(),
    designCode: getDesignCode(designContext),
    ...event,
  }

  if (typeof window !== 'undefined') {
    let trace = window.__CKD_CONDITION_ACTION_TRACE__ || []
    trace.push(traceEvent)
    if (trace.length > MAX_TRACE_SIZE) {
      trace = trace.slice(trace.length - MAX_TRACE_SIZE)
    }
    window.__CKD_CONDITION_ACTION_TRACE__ = trace
  }

  console.debug('[ConditionActionTrace]', traceEvent)
}
